<script>
  import { onMount, tick } from "svelte";
  import {
    addHash,
    decorateTransaction,
    transferTypes,
    localeOrderBy,
    slice,
    loadPersisted,
    savePersisted,
  } from "../../mjnUtils";
  import deburr from "lodash.deburr";
  import Pagination from "./pagination/Pagination.svelte";
  import Papa from "papaparse";

  let { http, toastr, search, openModal } = $props();

  const fields = addHash(
    [
      { name: "wszystkie", field: "$" },
      { name: "data", field: "data" },
      { name: "opis", field: "opis" },
      { name: "kontrahent", field: "kontrahent" },
      { name: "kwota", field: "kwota" },
      { name: "typ", field: "typ_text" },
      { name: "osoba", field: "idcz_text" },
      { name: "projekt", field: "idpr_text" },
      { name: "info", field: "info" },
    ],
    "name",
  );

  const transferIcons = {
    1: "fas fa-money-check-alt",
    2: "fas fa-piggy-bank",
    3: "fas fa-hand-holding-usd",
  };

  let transactions = $state([]);
  let projects = $state([]);
  let members = $state([]);
  let searchText = $state("");
  let searchFieldName = $state(loadPersisted("transfers", "searchFieldName", "wszystkie"));
  let activeTab = $state(loadPersisted("transfers", "activeTab", 1));
  let sortType = $state(loadPersisted("transfers", "sortType", "data"));
  let sortReverse = $state(loadPersisted("transfers", "sortReverse", true));
  let sortTypeK = $state("kwota");
  let sortReverseK = $state(true);
  let currentPage = $state(1);
  let currentPageK = $state(1);
  const itemsPerPage = 11;

  let chart = $state(null);

  $effect(() => {
    savePersisted("transfers", "searchFieldName", searchFieldName);
    savePersisted("transfers", "activeTab", activeTab);
    savePersisted("transfers", "sortType", sortType);
    savePersisted("transfers", "sortReverse", sortReverse);
  });

  let searchField = $derived(fields.hash[searchFieldName] || fields[0]);

  onMount(() => {
    chart = Highcharts.chart("chart", {
      chart: { type: "column" },
      legend: { enabled: false },
      title: { text: "Wpływy i wypłaty dla wybranych transakcji" },
      xAxis: {},
      yAxis: {
        title: { text: "Kwota" },
        labels: {
          formatter: function () {
            return this.axis.defaultLabelFormatter.call(this) + " zł";
          },
        },
      },
      tooltip: { valueDecimals: 2, valueSuffix: " zł" },
      credits: { enabled: false },
      colors: [
        "#2f7ed8",
        "#0d233a",
        "#8bbc21",
        "#910000",
        "#1aadce",
        "#492970",
        "#f28f43",
        "#77a1e5",
        "#c42525",
        "#a6c96a",
      ],
      plotOptions: { series: { stacking: true } },
      series: [
        { stack: "Suma", name: "Suma", id: "Suma", data: [] },
        { stack: "Suma", name: "Suma", linkedTo: "Suma", data: [] },
      ],
    });

    Promise.all([http.get("/api/projekt.php"), http.get("/api/czlonek.php")]).then(
      ([projectsResponse, membersResponse]) => {
        if (
          projectsResponse.data.status == 200 &&
          membersResponse.data.status == 200
        ) {
          projects = addHash(projectsResponse.data.records, "idpr");
          members = addHash(membersResponse.data.records, "idcz");
          loadTransactions();
        } else {
          toastr.error(
            "Błąd: " + projectsResponse.data.message + membersResponse.data.message,
          );
        }
      },
      () => {
        toastr.error("Błąd połączenia");
      },
    );
  });

  function loadTransactions() {
    http.get("/api/transakcja.php").then(
      (response) => {
        if (response.data.status == 200) {
          const loaded = addHash(response.data.records, "idtr");
          for (const transaction of loaded) {
            decorateTransaction(transaction, members, projects, transferTypes);
          }
          transactions = loaded;
          currentPage = 1;
        } else {
          toastr.error("Błąd: " + response.data.message);
        }
      },
      () => {
        toastr.error("Błąd połączenia");
      },
    );
  }

  function setSearchField(option) {
    searchFieldName = option.name;
  }

  function updateTabs(newTab) {
    activeTab = newTab;
  }

  function reflowChart() {
    setTimeout(() => {
      if (chart) chart.reflow();
    }, 0);
  }

  $effect(() => {
    if (!chart) return;
    if (activeTab == 2) {
      const binsYearP = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      const binsYearN = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      for (const transaction of filteredTransactions) {
        if (transaction.kwota < 0) {
          binsYearN[transaction.month - 1] += transaction.kwota;
        } else {
          binsYearP[transaction.month - 1] += transaction.kwota;
        }
      }
      chart.xAxis[0].setCategories([
        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
        "Lipiec", "Sierpień", "Wrzesień", "Pażdziernik", "Listopad", "Grudzień",
      ]);
      chart.series[0].setData(binsYearP, false, false);
      chart.series[1].setData(binsYearN, true, true);
      reflowChart();
    }
    if (activeTab == 3) {
      const binsMonthP = new Array(31).fill(0);
      const binsMonthN = new Array(31).fill(0);
      for (const transaction of filteredTransactions) {
        if (transaction.kwota < 0) {
          binsMonthN[transaction.day - 1] += transaction.kwota;
        } else {
          binsMonthP[transaction.day - 1] += transaction.kwota;
        }
      }
      chart.xAxis[0].setCategories(
        Array.from({ length: 31 }, (_, i) => i + 1),
      );
      chart.series[0].setData(binsMonthP, false, false);
      chart.series[1].setData(binsMonthN, true, true);
      reflowChart();
    }
  });

  function associateTransaction(transaction, membersList, projectsList) {
    transaction.idcz = null;
    transaction.idpr = null;
    for (const member of membersList) {
      if (transaction.opis) {
        if (
          transaction.opis.toLowerCase().indexOf(member.nazwisko.toLowerCase()) != -1 &&
          transaction.opis.toLowerCase().indexOf(member.imie.toLowerCase()) != -1
        ) {
          transaction.idcz = member.idcz;
          break;
        }
        if (
          deburr(transaction.opis).toLowerCase().indexOf(deburr(member.nazwisko).toLowerCase()) != -1 &&
          deburr(transaction.opis).toLowerCase().indexOf(deburr(member.imie).toLowerCase()) != -1
        ) {
          transaction.idcz = member.idcz;
          break;
        }
      }
      if (transaction.kontrahent) {
        if (
          transaction.kontrahent.toLowerCase().indexOf(member.nazwisko.toLowerCase()) != -1 &&
          transaction.kontrahent.toLowerCase().indexOf(member.imie.toLowerCase()) != -1
        ) {
          transaction.idcz = member.idcz;
          break;
        }
        if (
          deburr(transaction.kontrahent).toLowerCase().indexOf(deburr(member.nazwisko).toLowerCase()) != -1 &&
          deburr(transaction.kontrahent).toLowerCase().indexOf(deburr(member.imie).toLowerCase()) != -1
        ) {
          transaction.idcz = member.idcz;
          break;
        }
      }
    }
    for (const project of projectsList) {
      if (transaction.opis) {
        if (
          transaction.opis.toLowerCase().indexOf(project.nazwa.toLowerCase()) != -1 &&
          transaction.opis.toLowerCase().indexOf("grant") != -1
        ) {
          transaction.idpr = project.idpr;
          break;
        }
      }
    }
    if (transaction.kwota < 0) {
      transaction.typ = 1;
    } else {
      if (transaction.opis) {
        if (transaction.idcz == null) {
          if (
            transaction.opis.toLowerCase().indexOf("skład") != -1 ||
            transaction.opis.toLowerCase().indexOf("sklad") != -1
          ) {
            transaction.typ = 2;
          } else {
            transaction.typ = 3;
          }
        } else {
          if (transaction.opis.toLowerCase().indexOf("darowiz") != -1) {
            transaction.typ = 3;
          } else {
            transaction.typ = 2;
          }
        }
      } else {
        transaction.typ = 3;
      }
    }
    transaction.info = "";
    if (transaction.typ == 2 && transaction.idcz == null) {
      transaction.info = "Uwaga!";
    }
    if (transaction.typ == 3 && transaction.idcz != null) {
      transaction.info = "Uwaga!";
    }
  }

  function cleanTransaction(transaction) {
    delete transaction.nrRef;
    delete transaction.rachunek;
    delete transaction.referencje;
    delete transaction.rodzaj;
    delete transaction.kod;
    delete transaction.saldo;
    delete transaction.data_o;
  }

  function openFileBNP(event) {
    const file = event.target.files[0];
    if (!window.FileReader) {
      toastr.error("Uzywasz za starej przegladarki, import jest niemożliwy.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const bnpHeader =
        "Data transakcji;Kwota;Opis transakcji;Nazwa i adres kontrahenta;Typ transakcji;Rachunek kontrahenta;Referencje zleceniodawcy;Kod transakcji;Numer referencyjny transakcji";
      const contents = loadEvent.target.result;
      if (contents.indexOf(bnpHeader) == 0) {
        parseCsv(contents, "BNP");
      } else {
        toastr.error(
          "Błąd podczas importu, importowany plik nie wygląda na poprawny eksport z banku BNP Paribas!",
        );
      }
    };
    reader.readAsText(file);
  }

  function openFilemBank(event) {
    const file = event.target.files[0];
    if (!window.FileReader) {
      toastr.error("Uzywasz za starej przegladarki, import jest niemożliwy.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const mbankHeader =
        "#Data księgowania;#Data operacji;#Opis operacji;#Tytuł;#Nadawca/Odbiorca;#Numer konta;#Kwota;#Saldo po operacji;";
      const mbankFooter = ";;;;;;#Saldo końcowe";
      const contents = loadEvent.target.result;
      const indexH = contents.indexOf(mbankHeader);
      if (indexH != -1) {
        const indexF = contents.indexOf(mbankFooter);
        parseCsv(contents.substr(indexH, indexF - indexH), "mBank");
      } else {
        toastr.error(
          "Błąd podczas importu, importowany plik nie wygląda na poprawny eksport z mBanku! Użyj opcji Płatności|Historia|Zestawienie operacji.",
        );
      }
    };
    reader.readAsText(file, "CP1250");
  }

  function parseCsv(contents, bank) {
    Papa.parse(contents, {
      header: true,
      skipEmptyLines: "greedy",
      dynamicTyping: (header) => header == "kwota",
      transformHeader: (header) => {
        const map = {
          "Data księgowania": "data",
          "Data transakcji": "data",
          Kwota: "kwota",
          "Nazwa transakcji i opis": "opis",
          "Opis transakcji": "opis",
          "Nazwa i adres kontrahenta": "kontrahent",
          "Rodzaj transakcji": "rodzaj",
          "Typ transakcji": "rodzaj",
          "Rachunek kontrahenta": "rachunek",
          "Referencje zleceniodawcy": "referencje",
          "Kod operacji": "kod",
          "Numer referencyjny": "nrRef",
          "#Data księgowania": "data",
          "#Data operacji": "data_o",
          "#Opis operacji": "rodzaj",
          "#Tytuł": "opis",
          "#Nadawca/Odbiorca": "kontrahent",
          "#Numer konta": "rachunek",
          "#Kwota": "kwota",
          "#Saldo po operacji": "saldo",
        };
        // Papaparse's header-dedup pass runs transformHeader a second time on
        // its own output, so unmapped/already-mapped headers must round-trip
        // unchanged instead of becoming undefined.
        return map[header] ?? header;
      },
      transform: (value, col) => {
        if (col == "kwota") {
          return value.replace(",", ".").replace(" ", "");
        } else {
          return value.replace(/\s\s+/g, " ");
        }
      },
      error: (err, file, inputElem, reason) => {
        console.log(err, file, inputElem, reason);
      },
      complete: (results) => {
        for (let i = 0; i < results.data.length; i++) {
          delete results.data[i]["undefined"];
          delete results.data[i][""];
        }
        if (
          typeof results.data[0].opis == "undefined" ||
          typeof results.data[0].kontrahent == "undefined" ||
          typeof results.data[0].kwota == "undefined"
        ) {
          toastr.error("Błąd podczas importu, sprawdź czy wybrałeś dobry plik csv!");
        } else {
          const imported = results.data;
          if (
            confirm(
              "Ropoznano " + imported.length + " rekordów z banku " + bank + ". Zaimportować?",
            )
          ) {
            for (const row of imported) {
              if (row.opis == " " || row.opis == null) {
                row.opis = row.rodzaj;
              }
              cleanTransaction(row);
              associateTransaction(row, members, projects);
              row.bank = bank;
            }
            http.post("/api/transakcja.php?method=bulk", imported).then(
              (response) => {
                if (response.data.status == 200) {
                  toastr.info("Zaimportowano " + imported.length + " transakcji");
                  loadTransactions();
                } else {
                  toastr.error("Błąd: " + response.data.message);
                }
              },
              () => {
                toastr.error("Błąd połączenia");
              },
            );
          }
        }
      },
    });
  }

  function deleteImport() {
    openModal("TransferImportDeleteModal", { http, toastr }).then(
      (dataImp) => {
        if (!dataImp) return;
        http
          .delete("/api/transakcja-import.php", { params: { data_imp: dataImp } })
          .then(
            (response) => {
              if (response.data.status == 200) {
                toastr.info("Usunięcie importu zakończone sukcesem");
                loadTransactions();
              } else {
                toastr.error("Błąd: " + response.data.message);
              }
            },
            () => {
              toastr.error("Błąd połączenia");
            },
          );
      },
      () => {},
    );
  }

  function editTransfer(transfer) {
    openModal("TransferEditModal", {
      transfer,
      members: sortedMembers,
      projects: sortedProjects,
      types: transferTypes,
    }).then(
      (editedItem) => {
        if (!editedItem) return;
        http.put("/api/transakcja.php", editedItem).then(
          (response) => {
            if (response.data.status == 200) {
              toastr.info("Edycja zakończona sukcesem");
              decorateTransaction(editedItem, members, projects, transferTypes);
              transactions = transactions.map((t) =>
                t.idtr === editedItem.idtr ? editedItem : t,
              );
            } else {
              toastr.error("Błąd: " + response.data.message);
            }
          },
          () => {
            toastr.error("Błąd połączenia");
          },
        );
      },
      () => {},
    );
  }

  let searchObject = $derived({ [searchField.field]: searchText });
  let filteredTransactions = $derived(search(transactions, searchObject, fields.hash));
  let sortedTransactions = $derived(localeOrderBy(filteredTransactions, sortType, sortReverse));
  let totalItems = $derived(filteredTransactions.length);
  let numPages = $derived(Math.max(1, Math.ceil(totalItems / itemsPerPage)));
  let pagedTransactions = $derived(slice(sortedTransactions, itemsPerPage, currentPage));

  let sortedMembers = $derived(localeOrderBy(members, "nazwisko", false));
  let sortedProjects = $derived(localeOrderBy(projects, "nazwa", false));

  let groupedTransactions = $derived.by(() => {
    const map = new Map();
    for (const transaction of filteredTransactions) {
      const existing = map.get(transaction.kontrahent);
      if (existing === undefined) {
        map.set(transaction.kontrahent, {
          kontrahent: transaction.kontrahent,
          kwota: transaction.kwota,
          liczba: 1,
        });
      } else {
        existing.kwota += transaction.kwota;
        existing.liczba++;
      }
    }
    return Array.from(map.values());
  });
  let sortedGrouped = $derived(localeOrderBy(groupedTransactions, sortTypeK, sortReverseK));
  let totalItemsK = $derived(groupedTransactions.length);
  let numPagesK = $derived(Math.max(1, Math.ceil(totalItemsK / itemsPerPage)));
  let pagedGrouped = $derived(slice(sortedGrouped, itemsPerPage, currentPageK));

  $effect(() => {
    searchText;
    searchFieldName;
    currentPage = 1;
    currentPageK = 1;
  });

  function setSort(field) {
    if (sortType === field) {
      sortReverse = !sortReverse;
    } else {
      sortType = field;
      sortReverse = false;
    }
  }

  function setSortK(field) {
    if (sortTypeK === field) {
      sortReverseK = !sortReverseK;
    } else {
      sortTypeK = field;
      sortReverseK = false;
    }
  }

  let menuOpen = $state(false);
</script>

<svelte:window onclick={() => { menuOpen = false; }} />

<h3><i class="fas fa-credit-card fa-lg"></i>&nbsp;&nbsp;Przelewy
	<div class="btn-toolbar pull-right">
		<button class="btn btn-primary btn-sm pull-right" onclick={deleteImport} title="Otwiera okienko z listą importów transakcji. Można wybrać dowolny import i skasować transakcje w nim zaimportowane z bazy. W tym okienku są wszystkie informacje o importach, możemy więc w nim podejrzeć też czy jakieś dni nie zostały zaimportowane dwa razy itp."><i class="fas fa-trash" aria-hidden="true"></i>
	        <span class="hidden-xs">&nbsp;Skasuj import...</span>
		</button>
		<label class="btn btn-primary btn-file btn-sm pull-right" title="Import danych o transakcjach z pliku tekstowego .csv w formacie mBank. Wyeksportowany wcześniej ze strony Banku plik (opcją Płatności|Historia|Zestawienie operacji) wybierz w okienku, a transakcje pokażą się poniżej. System próbuje skojarzyć transakcje z Członkami Stowarzyszenia, ale nie zawsze się to udaje, dlatego warto przejrzeć zaimportowane transakcje i poprawić skojarzenia ręcznie. Skojarzenia są ważne m.in. przy liczeniu składek."><i class="fas fa-download" aria-hidden="true"></i>
			<span class="hidden-xs">&nbsp;Importuj z mBanku...</span>
			<input type="file" onchange={openFilemBank} style="display: none;">
		</label>
		<label class="btn btn-primary btn-file btn-sm pull-right" title="Import danych o transakcjach z pliku tekstowego .csv w formacie BNP. Wyeksportowany wcześniej ze strony Banku plik wybierz w okienku, a transakcje pokażą się poniżej. System próbuje skojarzyć transakcje z Członkami Stowarzyszenia, ale nie zawsze się to udaje, dlatego warto przejrzeć zaimportowane transakcje i poprawić skojarzenia ręcznie. Skojarzenia są ważne m.in. przy liczeniu składek."><i class="fas fa-download" aria-hidden="true"></i>
			<span class="hidden-xs">&nbsp;Importuj z BNP...</span>
			<input type="file" onchange={openFileBNP} style="display: none;">
		</label>
	</div>
</h3>
<div class="clearfix"></div>
<div class="text-left"><small><i class="fa fa-random" aria-hidden="true"></i>&nbsp;Mamy {transactions.length} transakcji w bazie{#if searchText != ""}, {filteredTransactions.length} przefiltrowanych poniżej{/if}.

</small><p></p></div>

<form>
    <div class="form-group">
      <div class="input-group">
        <div class="input-group-addon"><i class="fa fa-search"></i></div>
        <input type="text" class="form-control input-sm" placeholder="Wyszukaj transakcję, datę, itd." bind:value={searchText}>
		<div class="input-group-btn" class:open={menuOpen}>
			<button type="button" class="btn btn-default btn-sm dropdown-toggle" aria-haspopup="true" onclick={(e) => { e.stopPropagation(); menuOpen = !menuOpen; }}><span class="hidden-xs">Przeszukaj: </span>{searchField.name}&nbsp;&nbsp;<span class="caret"></span></button>
			<ul class="dropdown-menu dropdown-menu-right" role="menu">
		        {#each fields as option}<li role="menuitem" onclick={() => { setSearchField(option); menuOpen = false; }}><a href="#" onclick={(e) => e.preventDefault()}>{option.name}</a></li>{/each}
	      	</ul>
		</div>
      </div>
    </div>
</form>

<div>
<ul class="nav nav-tabs">
  	<li class={activeTab==1 ? "active" : ""} onclick={() => updateTabs(1)}><a href="#" onclick={(e) => e.preventDefault()}>Transakcje</a></li>
	<li class={activeTab==2 ? "active" : ""} onclick={() => updateTabs(2)}><a href="#" onclick={(e) => e.preventDefault()}><span class="hidden-xs">Wykres </span>roczny</a></li>
	<li class={activeTab==3 ? "active" : ""} onclick={() => updateTabs(3)}><a href="#" onclick={(e) => e.preventDefault()}><span class="hidden-xs">Wykres </span>miesięczny</a></li>
	<li class={activeTab==4 ? "active" : ""} onclick={() => updateTabs(4)}><a href="#" onclick={(e) => e.preventDefault()}>Kontrahenci</a></li>
</ul>
</div>

{#if activeTab==1}
<div>

	<table class="table table-condensed table-hover table-striped">

	<thead class="thead-default">
	  <tr>
		<th>
		  <a href="#" onclick={(e) => { e.preventDefault(); setSort("data"); }}>
		  Data
		{#if sortType == "data" && !sortReverse}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortType == "data" && sortReverse}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th>
		  <a href="#" onclick={(e) => { e.preventDefault(); setSort("opis"); }}>
		  Opis transakcji
		  {#if sortType == "opis" && !sortReverse}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortType == "opis" && sortReverse}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th>
		  <a href="#" onclick={(e) => { e.preventDefault(); setSort("kwota"); }}>
		  Kwota
		  {#if sortType == "kwota" && !sortReverse}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortType == "kwota" && sortReverse}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th class="hidden-xs hidden-sm hidden-md">
		  <a href="#" onclick={(e) => { e.preventDefault(); setSort("typ_text"); }}>
		  Typ
		  {#if sortType == "typ_text" && !sortReverse}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortType == "typ_text" && sortReverse}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th class="hidden-xs hidden-sm">
		  <a href="#" onclick={(e) => { e.preventDefault(); setSort("idcz_sort_text"); }}>
		  Osoba
		  {#if sortType == "idcz_sort_text" && !sortReverse}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortType == "idcz_sort_text" && sortReverse}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th class="hidden-xs hidden-sm hidden-md">
		  <a href="#" onclick={(e) => { e.preventDefault(); setSort("info"); }}>
		  Info
		  {#if sortType == "info" && !sortReverse}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortType == "info" && sortReverse}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th>
          Operacje
        </th>
	  </tr>
	</thead>

	<tbody>
	  {#each pagedTransactions as ts (ts.idtr)}
	  <tr>
		<td>
		<i class="{transferIcons[ts.typ]} fa-lg fa-fw" aria-hidden="true" style="margin: 6px 0px 8px 0px;"></i>&nbsp;&nbsp;{ts.data}
		</td>
		<td>
			{ts.opis}
			<div class="small text-muted">
				<span><i class="fas fa-university fa-fw" aria-hidden="true"></i>{ts.bank}</span>
				<span><i class="fas fa-id-badge fa-fw" aria-hidden="true"></i>{ts.kontrahent}</span>
			</div>
			<div>
				{#if ts.idcz_text}<span class="visible-xs visible-sm small text-muted"><i class="fas fa-user fa-fw" aria-hidden="true"></i><a href={"/member-detail/" + ts.idcz} title="Podgląd danych osoby">{ts.idcz_text}</a></span>{/if}
				{#if ts.idpr_text}<span class="small text-muted"><i class="fas fa-funnel-dollar fa-fw" aria-hidden="true"></i><a href={"/project-detail/" + ts.idpr} title="Podgląd danych projektu">{ts.idpr_text}</a></span>{/if}
			</div>
		</td>
		<td>
		{ts.kwota}
		</td>
		<td class="hidden-xs hidden-sm hidden-md">
		{ts.typ_text}
		</td>
		<td class="hidden-xs hidden-sm">
			<a href={"/member-detail/" + ts.idcz} title="Podgląd danych osoby">{ts.idcz_text}</a>
		</td>
		<td class="hidden-xs hidden-sm hidden-md">
		{ts.info}
		</td>
		<td>
        	<a href="#" onclick={(e) => { e.preventDefault(); editTransfer(ts); }}><i class="fas fa-pen-square fa-lg fa-fw"></i></a>
        </td>
	  </tr>
	  {/each}
	</tbody>

	</table>

	<Pagination {currentPage} {numPages} onPageChange={(page) => currentPage = page}/>

</div>
{/if}

<div style={activeTab==2 || activeTab==3 ? "" : "display:none"}>

    	<div id="chart"></div>

</div>

{#if activeTab==4}
<div>

	<table class="table table-condensed table-hover table-striped">
	<thead class="thead-default">
	  <tr>
		<th class="hidden-xs">
		  <a href="#" onclick={(e) => { e.preventDefault(); setSortK("kontrahent"); }}>
		  Kontrahent dla transakcji
		  {#if sortTypeK == "kontrahent" && !sortReverseK}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortTypeK == "kontrahent" && sortReverseK}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th>
		  <a href="#" onclick={(e) => { e.preventDefault(); setSortK("kwota"); }}>
		  Sumaryczna kwota transakcji
		  {#if sortTypeK == "kwota" && !sortReverseK}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortTypeK == "kwota" && sortReverseK}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>
		<th class="hidden-xs">
		  <a href="#" onclick={(e) => { e.preventDefault(); setSortK("liczba"); }}>
		  Liczba transakcji
		  {#if sortTypeK == "liczba" && !sortReverseK}<span class="fa fa-caret-down"></span>{/if}
		  {#if sortTypeK == "liczba" && sortReverseK}<span class="fa fa-caret-up"></span>{/if}
		  </a>
		</th>

	  </tr>
	</thead>

	<tbody>
	  {#each pagedGrouped as ts}
	  <tr>
		<td>
		<i class="fas fa-id-badge fa-fw fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;{ts.kontrahent}
		</td>
		<td>
		{ts.kwota.toFixed(2)}
		</td>
		<td>
		{ts.liczba}
		</td>
	  </tr>
	  {/each}
	</tbody>

	</table>

	<Pagination {currentPage} {numPages} onPageChange={(page) => currentPage = page}/>
</div>
{/if}
